import { validateAddress, validDateOfBirth, parseUSDayMonthYearDate, } from 'shared';
import { z } from 'zod';
export const personalInfoFormSchema = z
    .object({
    firstName: z.string().min(1, { message: "Can't be blank" }),
    lastName: z.string().min(1, { message: "Can't be blank" }),
    street1: z
        .string()
        .min(1, { message: "Can't be blank" })
        .refine((value) => !validateAddress(value), {
        message: 'PO Box cannot be used in the address.',
    }),
    street2: z
        .string()
        .optional()
        .refine((value) => !validateAddress(value ?? ''), {
        message: 'PO Box cannot be used in the address.',
    }),
    city: z.string().min(1, { message: "Can't be blank" }),
    state: z.string().min(1, { message: "Can't be blank" }),
    zip: z
        .string()
        .min(1, { message: "Can't be blank" })
        .length(5, { message: 'Must be 5 digits' })
        .regex(/^\d+$/, { message: 'Must be numeric' }),
    primaryPhone: z.string().optional(),
    homeNumberCheck: z.boolean().optional(),
    alternatePhone: z.string().optional(),
    dateOfBirth: z
        .string()
        .min(1, { message: "Can't be blank" })
        .length(10, { message: 'Must be a complete date' }),
    email: z
        .string()
        .min(1, { message: "Can't be blank" })
        .email({ message: 'Invalid email address' }),
    ssn: z.string().min(1, { message: "Can't be blank" }),
    annualIncome: z.string().refine((value) => {
        const numeric = Number(value.replace(/,/g, ''));
        return !!numeric && numeric > 0;
    }, { message: "Can't be blank" }),
    purchaseAmount: z.string().optional(),
    specialty: z.string().optional(),
    agreed: z.boolean().optional(),
})
    .superRefine((data, ctx) => {
    if (!data.homeNumberCheck && !data.primaryPhone) {
        ctx.addIssue({
            code: 'custom',
            message: "Mobile Number can't be blank",
            path: ['primaryPhone'],
        });
    }
    if (data.homeNumberCheck && !data.alternatePhone) {
        ctx.addIssue({
            code: 'custom',
            message: "Home Number can't be blank",
            path: ['alternatePhone'],
        });
    }
    const dateOfBirth = parseUSDayMonthYearDate(data.dateOfBirth);
    if (data.dateOfBirth?.length === 10 && !validDateOfBirth(dateOfBirth)) {
        ctx.addIssue({
            code: 'custom',
            message: 'Date of birth year must be after 1900 and applicant must be at least 18 years old',
            path: ['dateOfBirth'],
        });
    }
});
/**
 * `specialty` is conditionally required (only when `practice.locationId` is
 * nil) by the caller — see `buildPersonalInfoFormSchema` in
 * `PersonalInfoForm.tsx` which layers this refinement on top of the base
 * schema depending on the `practice` prop.
 */
export const buildPersonalInfoFormSchema = (specialtyRequired) => specialtyRequired
    ? personalInfoFormSchema.superRefine((data, ctx) => {
        if (!data.specialty) {
            ctx.addIssue({
                code: 'custom',
                message: "Can't be blank",
                path: ['specialty'],
            });
        }
    })
    : personalInfoFormSchema;
